/**
 * Utility Functions
 * For Media Devoted | Created By Ciro Autiero
 */

import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge Tailwind classes
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format currency
 */
export function formatCurrency(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Currency code -> symbol for display (common ones) */
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  AUD: 'A$',
  CAD: 'C$',
  CHF: 'CHF',
  BRL: 'R$',
  MXN: 'MX$',
  NZD: 'NZ$',
  PLN: 'zł',
  NOK: 'kr',
  SEK: 'kr',
  CZK: 'Kč',
  HUF: 'Ft',
  RON: 'lei',
  TRY: '₺',
  SGD: 'S$',
  COP: '$',
  JPY: '¥',
  INR: '₹',
};

/**
 * Get display symbol for currency code. Falls back to code if unknown.
 */
export function getCurrencySymbol(currencyCode: string): string {
  if (!currencyCode || typeof currencyCode !== 'string') return '$';
  const code = currencyCode.toUpperCase().trim();
  return CURRENCY_SYMBOLS[code] ?? `${code} `;
}

/**
 * Format cost with appropriate precision for SMS pricing.
 * Uses 3–4 decimals for small amounts (e.g. $0.017) to avoid rounding errors.
 * @param value - Numeric value
 * @param currencyCode - ISO 4217 code (e.g. USD, EUR). Default: USD
 */
export function formatCost(value: number, currencyCode = 'USD'): string {
  if (!Number.isFinite(value)) return '—';
  const decimals = Math.abs(value) >= 1 ? 2 : Math.abs(value) >= 0.01 ? 3 : 4;
  const symbol = getCurrencySymbol(currencyCode);
  const absVal = Math.abs(value);
  if (value < 0) return `-${symbol}${absVal.toFixed(decimals)}`;
  return `${symbol}${value.toFixed(decimals)}`;
}

/**
 * Format number with commas
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

/**
 * Format percentage
 */
export function formatPercentage(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format date
 */
export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', options || {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * DD/MM/YYYY HH:mm (24h) per project date rules
 */
export function formatDateTimeGb(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return formatDate(d);
}

/**
 * Truncate string
 */
export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return `${str.slice(0, length)}...`;
}

/** Sleep/delay */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export { formatSendErrorDescription } from './send-errors';
export { getApiErrorMessage } from './api-error';

/** Debounce */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}
